import { RefObject, useEffect } from 'react';
import { useKeyboardShortcutsContext } from '../context/useKeyboardShortcutsContext';

type UseKeyboardShortcutArgs = {
  id: string;
  scopeRef?: RefObject<HTMLElement | null>;
  shouldHandle: (event: KeyboardEvent) => boolean;
  onTrigger: (event: KeyboardEvent) => void;
  // Capture-phase shortcut: pre-empts component-internal key handling, routes by focused element and swallows the matched event; requires scopeRef. See KeyboardShortcutRegistration.
  capture?: boolean;
};

// Registers a shortcut for the lifetime of the calling component; pass stable `shouldHandle` / `onTrigger` (useCallback) to avoid re-registering on every render.
export const useKeyboardShortcut = ({
  id,
  scopeRef,
  shouldHandle,
  onTrigger,
  capture,
}: UseKeyboardShortcutArgs) => {
  const { registerShortcut } = useKeyboardShortcutsContext();

  useEffect(() => {
    const unregister = registerShortcut({
      id,
      scopeRef,
      shouldHandle,
      onTrigger,
      capture,
    });

    return unregister;
  }, [id, registerShortcut, scopeRef, shouldHandle, onTrigger, capture]);
};
